import {useCallback, useEffect, useRef, useState} from 'react';

export type MediaLoadStatus = 'idle' | 'loading' | 'loaded' | 'error' | 'retrying';

interface UseMediaLoadOptions {
    src?: string;
    maxRetries?: number;
    baseDelayMs?: number;
    onLoaded?: () => void;
    onFailed?: () => void;
}

function appendRetryParam(src: string, attempt: number): string {
    if (src.startsWith('data:') || src.startsWith('blob:')) {
        return src;
    }

    try {
        const url = new URL(src, window.location.href);
        url.searchParams.set('_retry', String(attempt));
        url.searchParams.set('_t', String(Date.now()));
        return url.toString();
    } catch {
        const separator = src.includes('?') ? '&' : '?';
        return `${src}${separator}_retry=${attempt}&_t=${Date.now()}`;
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useMediaLoad({src, maxRetries = 3, baseDelayMs = 1000, onLoaded, onFailed}: UseMediaLoadOptions) {
    const [status, setStatus] = useState<MediaLoadStatus>('idle');
    const [effectiveSrc, setEffectiveSrc] = useState<string | undefined>(undefined);
    const attemptRef = useRef(0);
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const onLoadedRef = useRef(onLoaded);
    const onFailedRef = useRef(onFailed);

    onLoadedRef.current = onLoaded;
    onFailedRef.current = onFailed;

    const clearRetryTimer = useCallback(() => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = undefined;
        }
    }, []);

    useEffect(() => {
        clearRetryTimer();
        attemptRef.current = 0;

        if (!src) {
            setStatus('error');
            setEffectiveSrc(undefined);
            return undefined;
        }

        setStatus('loading');
        setEffectiveSrc(src);

        return clearRetryTimer;
    }, [src, clearRetryTimer]);

    const handleLoad = useCallback(() => {
        clearRetryTimer();
        setStatus('loaded');
        onLoadedRef.current?.();
    }, [clearRetryTimer]);

    const scheduleRetry = useCallback(
        (nextAttempt: number) => {
            if (!src) {
                return;
            }

            setStatus('retrying');
            const delay = baseDelayMs * 2 ** (nextAttempt - 1);

            retryTimeoutRef.current = setTimeout(() => {
                attemptRef.current = nextAttempt;
                setEffectiveSrc(appendRetryParam(src, nextAttempt));
                setStatus('loading');
            }, delay);
        },
        [src, baseDelayMs]
    );

    const handleError = useCallback(() => {
        clearRetryTimer();

        const nextAttempt = attemptRef.current + 1;
        if (nextAttempt <= maxRetries && src) {
            scheduleRetry(nextAttempt);
            return;
        }

        setStatus('error');
        onFailedRef.current?.();
    }, [maxRetries, src, clearRetryTimer, scheduleRetry]);

    const isLoading = status === 'loading' || status === 'retrying';
    const hasFailed = status === 'error';

    return {
        status,
        effectiveSrc,
        handleLoad,
        handleError,
        isLoading,
        hasFailed,
    };
}
